import React from "react";
import { useTranslation } from "react-i18next";
import SeoHead from "@/components/SeoHead";
import PageShell from "@/components/PageShell";
import PageBanner from "@/components/PageBanner";
import { Flame, Coffee, Sparkles, TrendingUp, Handshake, Globe, Star } from "lucide-react";

const BANNER = "https://images.unsplash.com/photo-1442550528053-c431ecb55509?auto=format&fit=crop&w=2000&q=80";
const LOGO_CC = "https://customer-assets.emergentagent.com/job_napoli-artigianalita/artifacts/ff6wp22h_logo%20CC.png";
const ROASTER_IMG = "https://customer-assets-39nsmqrw.emergentagent.net/job_napoli-artigianalita/artifacts/f5keyviz_WhatsApp%20Image%202026-08-03%20at%2010.56.59.jpeg";
const TESORI_BAG = "/tesori-bag.jpg";

// Per item structural configuration (icons, images). Text comes from i18n by index.
const ITEM_META = [
  { icon: Sparkles, image: "/tempo-1934-camillo.jpg", imageStyle: "cover", imageAspect: "4/3" },
  { icon: Coffee, image: LOGO_CC, imageStyle: "logo" },
  { icon: Flame, image: ROASTER_IMG, imageStyle: "cover", imageAspect: "16/10" },
  { icon: TrendingUp, image: "/tempo-crescita-castello.jpg", imageStyle: "cover", imageAspect: "16/9" },
  { icon: Handshake, image: TESORI_BAG, imageStyle: "cover", imageAspect: "5/6" },
  { icon: Globe, image: "/tempo-2025-gaetano-expo.jpg", imageStyle: "cover", imageAspect: "3/4" },
  { icon: Star, image: "/tempo-2026-locandina.jpg", imageStyle: "cover", imageAspect: "2/3" },
];

const TimelineMedia = ({ item }) => {
  if (!item.image) return null;

  if (item.imageStyle === "logo") {
    return (
      <div className="mb-5">
        <div
          className="relative rounded-xl overflow-hidden flex items-center justify-center px-6 py-7"
          style={{
            background: "radial-gradient(ellipse at center, #fbf5e9 0%, #f5ead6 55%, #ecdcbd 100%)",
            border: "1px solid rgba(91,54,33,0.15)",
            boxShadow: "inset 0 0 40px rgba(91,54,33,0.08)",
          }}
        >
          <div className="absolute inset-0 pointer-events-none opacity-25">
            {[
              { top: "12%", left: "10%", rot: -20 },
              { top: "22%", right: "12%", rot: 25 },
              { bottom: "18%", left: "16%", rot: 40 },
              { bottom: "24%", right: "10%", rot: -15 },
            ].map((b, i) => (
              <span
                key={i}
                className="absolute rounded-full"
                style={{
                  top: b.top,
                  left: b.left,
                  right: b.right,
                  bottom: b.bottom,
                  width: 12,
                  height: 17,
                  background: "#5b3621",
                  transform: `rotate(${b.rot}deg)`,
                }}
              />
            ))}
          </div>
          <img
            src={item.image}
            alt={item.title}
            className="relative h-24 sm:h-28 w-auto object-contain drop-shadow-sm"
            style={{ mixBlendMode: "multiply" }}
            loading="lazy"
          />
        </div>
        {item.caption && (
          <p className="mt-2 text-[11px] uppercase tracking-[0.2em] text-center font-semibold" style={{ color: "#c5371f" }}>
            {item.caption}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="mb-5">
      <div
        className="relative rounded-xl overflow-hidden group"
        style={{
          aspectRatio: item.imageAspect || "16/9",
          boxShadow: "0 12px 30px -12px rgba(42,22,12,0.35)",
          border: "1px solid rgba(91,54,33,0.15)",
        }}
      >
        <img
          src={item.image}
          alt={item.title}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          loading="lazy"
        />
        <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0) 55%, rgba(42,22,12,0.7) 100%)" }} />
        {item.caption && (
          <div className="absolute bottom-3 left-3 right-3">
            <span className="inline-block px-3 py-1.5 rounded-full text-[11px] font-semibold uppercase tracking-wider" style={{ background: "rgba(251,245,233,0.95)", color: "#c5371f" }}>
              {item.caption}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

const CaffeColaNelTempo = () => {
  const { t } = useTranslation();
  const items = t("tempo.items", { returnObjects: true }) || [];

  return (
    <PageShell>
      <SeoHead pageKey="tempo" image={BANNER} />
      <PageBanner
        eyebrow={t("tempo.banner.eyebrow")}
        title={t("tempo.banner.title")}
        subtitle={t("tempo.banner.subtitle")}
        image={BANNER}
      />

      <section className="section-y" style={{ background: "#fbf5e9" }}>
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <div className="divider-beans font-serif-display uppercase tracking-[0.3em] text-xs mb-5">
            {t("tempo.introEyebrow")}
          </div>
          <p className="text-base sm:text-lg leading-relaxed" style={{ color: "#3b2417" }}>
            {t("tempo.introP")}
          </p>
        </div>
      </section>

      <section className="pb-20" style={{ background: "#fbf5e9" }}>
        <div className="max-w-5xl mx-auto px-6 lg:px-8 relative">
          <div
            className="absolute left-6 lg:left-1/2 top-0 bottom-0 w-0.5 lg:-translate-x-1/2"
            style={{ background: "linear-gradient(180deg, rgba(197,55,31,0.0) 0%, rgba(197,55,31,0.9) 8%, rgba(197,55,31,0.9) 92%, rgba(197,55,31,0.0) 100%)" }}
          />

          <ol className="relative space-y-14 lg:space-y-16">
            {items.map((it, idx) => {
              const meta = ITEM_META[idx] || {};
              const Icon = meta.icon || Sparkles;
              const isLeft = idx % 2 === 0;
              const mediaItem = {
                image: meta.image,
                imageStyle: meta.imageStyle,
                imageAspect: meta.imageAspect,
                caption: it.caption,
                title: it.title,
              };
              return (
                <li key={idx} className="relative pl-16 lg:pl-0" data-testid={`timeline-${idx}`}>
                  <div
                    className="absolute left-6 lg:left-1/2 lg:-translate-x-1/2 top-1 w-6 h-6 rounded-full ring-4 flex items-center justify-center z-10"
                    style={{ background: "#c5371f", boxShadow: "0 0 0 6px #fbf5e9, 0 0 0 8px rgba(197,55,31,0.25)" }}
                  >
                    <div className="w-2 h-2 rounded-full bg-white" />
                  </div>

                  <div className="lg:grid lg:grid-cols-2 lg:gap-16 items-start">
                    <div className={`${isLeft ? "lg:pr-8 lg:text-right" : "lg:col-start-2 lg:pl-8"}`}>
                      <span className="font-serif-display font-bold text-2xl sm:text-3xl lg:text-4xl block" style={{ color: "#c5371f" }}>
                        {it.year}
                      </span>
                      <div
                        className={`mt-4 rounded-2xl p-6 lg:p-7 inline-block text-left ${isLeft ? "lg:text-right" : ""}`}
                        style={{
                          background: "#f5ead6",
                          border: "1px solid rgba(91,54,33,0.15)",
                          boxShadow: "0 20px 40px -25px rgba(42,22,12,0.35)",
                        }}
                      >
                        <TimelineMedia item={mediaItem} />

                        <div className={`flex items-center gap-3 mb-3 ${isLeft ? "lg:justify-end" : ""}`}>
                          <div
                            className={`w-10 h-10 rounded-full flex items-center justify-center ${isLeft ? "lg:order-2" : ""}`}
                            style={{ background: "#2a160c" }}
                          >
                            <Icon size={18} color="#f5ead6" />
                          </div>
                          <h3 className="font-serif-display font-bold text-lg sm:text-xl" style={{ color: "#2a160c" }}>
                            {it.title}
                          </h3>
                        </div>
                        <p className="text-sm sm:text-base leading-relaxed" style={{ color: "#5b3621" }}>
                          {it.text}
                        </p>
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      </section>
    </PageShell>
  );
};

export default CaffeColaNelTempo;
